import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bomb, Target, Zap, Dices, Layers, Gauge, ArrowRight, Gamepad2, Gift, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import AffiliateSignupDialog from "@/components/marketing/AffiliateSignupDialog";

const GAMES = [
  {
    icon: Bomb,
    name: "Digit Mines",
    payout: "up to ×9+ pot",
    tag: "Pick safe tiles, dodge the bomb, ride the pot and cash out whenever you want.",
    accent: "text-amber-400",
    ring: "border-amber-400/30 hover:border-amber-400/70",
    glow: "bg-amber-500/20",
  },
  {
    icon: Target,
    name: "Penalty Shootout",
    payout: "~×1.95 per goal",
    tag: "Shoot left or right with live market context right under the goal.",
    accent: "text-emerald-400",
    ring: "border-emerald-400/30 hover:border-emerald-400/70",
    glow: "bg-emerald-500/20",
  },
  {
    icon: Zap,
    name: "Boxing KO",
    payout: "~×1.9 per KO",
    tag: "Throw a rise or fall punch and watch the knockout play out live.",
    accent: "text-red-400",
    ring: "border-red-400/30 hover:border-red-400/70",
    glow: "bg-red-500/20",
  },
  {
    icon: Dices,
    name: "Dice Duel",
    payout: "up to ×9.5",
    tag: "High roll vs low roll — a head-to-head duel on real market ticks.",
    accent: "text-cyan-400",
    ring: "border-cyan-400/30 hover:border-cyan-400/70",
    glow: "bg-cyan-500/20",
  },
  {
    icon: Layers,
    name: "Red / Black Flip",
    payout: "~×1.95 per card",
    tag: "Call the flip — casino-style card reveal powered by real contracts.",
    accent: "text-rose-400",
    ring: "border-rose-400/30 hover:border-rose-400/70",
    glow: "bg-rose-500/20",
  },
  {
    icon: Gauge,
    name: "Green Light",
    payout: "~×1.9 per run",
    tag: "Time your run and go when it's green — quick-fire market action.",
    accent: "text-lime-400",
    ring: "border-lime-400/30 hover:border-lime-400/70",
    glow: "bg-lime-500/20",
  },
];

const ArcadeShowcase = () => {
  const navigate = useNavigate();
  const [signupOpen, setSignupOpen] = useState(false);

  return (
    <section
      id="arcade"
      data-testid="arcade-showcase-section"
      className="relative py-24 lg:py-32 border-t border-vts-line overflow-hidden"
    >
      <div className="absolute inset-0 vts-grid-bg opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-64 w-[36rem] rounded-full bg-vts-blue/10 blur-[110px]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="text-xs uppercase tracking-[0.25em] text-vts-blue font-semibold flex items-center gap-2">
              <Gamepad2 className="h-4 w-4" /> The VTS Arcade
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-amber-400/50 bg-amber-500/15">
              <Flame className="h-3 w-3 text-amber-400" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-bold">
                Most played
              </span>
            </div>
            <div className="free-badge inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-emerald-400/50 bg-emerald-500/15">
              <Gift className="h-3 w-3 text-emerald-400" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold">
                Free to play
              </span>
            </div>
          </div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl text-white tracking-tighter">
            Trading that feels<br />
            <span className="animate-gradient-text">like a game.</span>
          </h2>
          <p className="mt-5 text-vts-silver/70 text-base font-body leading-relaxed">
            Ten arcade games, each one powered by <span className="text-white font-medium">real Deriv contracts</span> —
            every shot, flip and tile is a real market trade with real payouts.
            Practice on the free $10,000 demo first, then play for real when you're ready.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GAMES.map((g, idx) => (
            <div
              key={g.name}
              data-testid={`arcade-showcase-card-${idx}`}
              className={`arcade-card relative rounded-xl border bg-vts-surface/60 backdrop-blur-sm p-6 overflow-hidden ${g.ring}`}
            >
              <div className={`arcade-card-glow absolute -top-10 -right-10 h-32 w-32 rounded-full blur-[50px] ${g.glow}`} style={{ animationDelay: `${idx * 0.5}s` }} />
              <div className="relative">
                <div className="flex items-center justify-between mb-5">
                  <div className={`h-12 w-12 rounded-lg border border-vts-line bg-black/50 flex items-center justify-center animate-float ${g.accent}`} style={{ animationDelay: `${idx * 0.35}s` }}>
                    <g.icon className="h-6 w-6" />
                  </div>
                  <span className={`text-[11px] font-mono font-black tracking-wide border rounded-full px-2.5 py-1 ${g.accent} border-current/40 bg-black/40`}
                    style={{ textShadow: "0 0 10px currentColor" }}>
                    {g.payout}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-xl text-white tracking-tight mb-2">
                  {g.name}
                </h3>
                <p className="text-sm text-vts-silver/70 leading-relaxed font-body">
                  {g.tag}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-[10px] font-mono text-vts-silver/40 uppercase tracking-wider">
                    Real Deriv contract
                  </span>
                  <button
                    data-testid={`arcade-showcase-play-${idx}`}
                    onClick={() => navigate("/trade?connect=1")}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md border transition hover:bg-white/5 ${g.accent} border-current/40`}
                  >
                    Play free <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Button
            data-testid="arcade-showcase-signup-btn"
            onClick={() => setSignupOpen(true)}
            size="lg"
            className="btn-shimmer bg-vts-blue hover:bg-vts-blueHover text-white h-12 px-7 rounded-md font-semibold text-sm"
          >
            Create a free account & play
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <button
            data-testid="arcade-showcase-open-btn"
            onClick={() => navigate("/trade?connect=1")}
            className="inline-flex items-center gap-2 text-vts-silver hover:text-white transition font-medium text-sm px-5 py-3 border border-vts-line rounded-md hover:bg-vts-surface hover:border-vts-blue"
          >
            Open the Arcade <span className="text-vts-blue">→</span>
          </button>
          <span className="text-[11px] text-vts-silver/50 font-mono">
            Stakes from $0.35 · demo mode included · built-in loss limits
          </span>
        </div>
      </div>
      <AffiliateSignupDialog open={signupOpen} onOpenChange={setSignupOpen} />
    </section>
  );
};

export default ArcadeShowcase;
